import '../../../assets/okta/css/okta-sign-in.min.css';
import React, { FC, useMemo, useCallback } from 'react';
import OktaSignInWidget, { EventContext, EventErrorContext } from '@okta/okta-signin-widget';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useAnalytics, useScopedSelector, scrollIntoView } from 'ia-react-core';
import Configuration from '../../../utils/Configuration';
import { WidgetGlobalStyles } from './SignIn.styles';
import useHandleOnSuccess from './useHandleOnSuccess';
import onError from './onError';
import onAfterError from './onAfterError';
import { getSignInConfiguration } from './getSignInConfiguration';
import { setCode, setTitle, setErrorCounter } from '../LoginPage.actions';
import { GetLoginTitle } from '../LoginUtils';
import LoginPageState from '../interfaces/LoginPageState';
import { getLanguage } from '../../../utils/getLanguage';

export interface SignInProps {
  disableSignUp?: boolean;
}

const SignIn: FC<SignInProps> = ({ disableSignUp = false }) => {
  const { t } = useTranslation('LoginPage');
  const { errorCounter, username } = useScopedSelector((state: LoginPageState) => state);
  const config = getSignInConfiguration(t, disableSignUp);
  const configCurrent = Configuration.Current();
  const lang = getLanguage();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const augmentedConfiguration = useMemo(() => ({ ...config, el: '#login-widget', defaultCountryCode: 'CA' }), [config.baseUrl, lang]);
  const widget: OktaSignInWidget = useMemo(() => (new OktaSignInWidget(augmentedConfiguration)), [augmentedConfiguration]);
  const dispatch = useDispatch();
  const handleonSuccess = useHandleOnSuccess();
  const { track } = useAnalytics();

  const doTrack = useCallback(() => {
    const trackOption = { event: new MouseEvent('click', {}), scope: 'Sign In', values: {} };
    track(trackOption);
  }, [track]);

  const setAttributeDataById = (id: string, dataID: string) => {
    const elem = document.getElementById(id);
    if (elem) {
      elem.setAttribute('data-testid', dataID);
    }
  };

  const setAttributeDataByClassName = (classNames: string, attrName: string, attrValue: string, attrIndex = 0) => {
    const elems = document.getElementsByClassName(classNames);
    if (elems[attrIndex]) {
      elems[attrIndex].setAttribute(attrName, attrValue);
    }
  };

  const handleClick = (e: Event, url: string) => {
    e.preventDefault();
    e.stopPropagation();
    window.location.href = url;
  };

  const onAfterRender = useCallback((context: EventContext) => {
    let node: HTMLElement = document.querySelector("[data-testid='MainHeader']");
    if (node == null) {
      node = document.querySelector("[data-testid='MainContent']");
    }
    scrollIntoView(node);
    let controllerName = context.controller;
    if (typeof context.controller === 'function') {
      controllerName = 'enroll-sms';
    }
    dispatch(setTitle(t(GetLoginTitle(controllerName))));
    dispatch(setCode(controllerName));

    const btn = document.getElementById('okta-signin-submit');
    if (btn != null) {
      btn.addEventListener('click', doTrack);
    }

    const btnTempPasswordLink = document.getElementsByClassName('button button-primary button-wide link-button')[0];
    if (btnTempPasswordLink != null) {
      btnTempPasswordLink.addEventListener('click', (e) => {
        handleClick(e, `https://${configCurrent.domainName}${configCurrent.publicUrl}change-password`);
      });
    }

    if (configCurrent.useIdPDiscovery && context.controller === 'idp-discovery') {
      setAttributeDataByClassName('link js-custom', 'style', 'display:block');
      setAttributeDataByClassName('link js-custom', 'style', 'display:none', 1);
      setAttributeDataByClassName('link js-forgot-password', 'style', 'display:none');
      setAttributeDataByClassName('o-form-input-name-remember', 'style', 'display:block');
      setAttributeDataByClassName('registration-container', 'style', 'display:block');
    } else if (configCurrent.useIdPDiscovery && context.controller === 'primary-auth') {
      setAttributeDataByClassName('link js-custom', 'style', 'display:none');
      setAttributeDataByClassName('link js-custom', 'style', 'display:block', 1);
      setAttributeDataByClassName('link js-forgot-password', 'style', 'display:block');
      setAttributeDataByClassName('o-form-input-name-remember', 'style', 'display:none');
      setAttributeDataByClassName('registration-container', 'style', 'display:none');
    } else {
      setAttributeDataByClassName('link js-custom', 'style', 'display:none', 1);
    }

    setAttributeDataById('okta-signin-submit', 'SignIn');
    setAttributeDataByClassName('button button-primary button-wide link-button', 'data-testid', 'TemporaryPasswordLink');
    setAttributeDataByClassName('link js-forgot-password', 'data-testid', 'ForgotPassword');
    setAttributeDataByClassName('link js-custom', 'data-testid', 'ForgotUsername');
    setAttributeDataByClassName('registration-link', 'data-testid', 'CreateAccount');
  }, [dispatch, t, doTrack, configCurrent.domainName, configCurrent.publicUrl, configCurrent.useIdPDiscovery]);

  const onAfterErrorCb = useCallback((context: EventContext, error: EventErrorContext) => {
    const trackOption = { event: new MouseEvent('click', {}), scope: 'Sign In', values: { login: 'fails' } };
    track(trackOption);
    if (onAfterError(context, error, errorCounter, username, dispatch, t)) {
      dispatch(setErrorCounter());
    }
  }, [dispatch, t, errorCounter, username, track]);

  React.useEffect(() => {
    widget.renderEl(augmentedConfiguration, handleonSuccess, onError).finally(() => { /* nothing */ });
    return () => {
      widget.remove();
    };
  }, [augmentedConfiguration, handleonSuccess, widget]);

  React.useEffect(() => {
    widget.on('afterRender', onAfterRender);
    widget.on('afterError', onAfterErrorCb);
    return () => {
      widget.off('afterRender');
      widget.off('afterError');
    };
  }, [widget, onAfterRender, onAfterErrorCb]);

  return (
    <>
      <WidgetGlobalStyles />
      <div id="login-widget" />
    </>
  );
};

export default SignIn;
